import { useMutation } from '@tanstack/react-query';
import {
  Col,
  MenuProps,
  Modal,
  Row,
  Spin,
  Tabs,
  TabsProps,
  Typography,
} from 'antd';
import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { SalesInfoApis, TAllocateUpdateJsonParams } from 'apis/SalesInfoApis';
import { Button } from 'components';
import { useTitle } from 'contexts/TitleProvider';
import { useUser } from 'contexts/UserProvider';
import useQueryParam from 'hooks/useQueryPrams';
import { usePromotionManager } from './hooks/usePromotionManager';
import {
  CreateDealPrice,
  IAllocatePriceParams,
  ICreateEInvoice,
  ICustomer,
  IDataGrouped,
  IGetAllocatePriceSAP,
  IGetOrderParams,
  IGetOrderProcessParams,
  IGetOrderTempByIdParams,
  IGoldSoldBuyOrder,
  IGoldSoldYBuyOrder,
  IOrder,
  IOrderProcess,
  IPrintFile,
  IPromotion,
  IPromotionExtended,
  IPromotionResult,
  Result,
} from 'interfaces/SalesInfo';
import Content from 'layouts/Content';
import path from 'routes/Path';
import {
  convertDateToBE,
  toastError,
  toastInfo,
  toastSuccess,
} from 'utils/constants/functions';
import DealPriceModal from './DealPriceModal';
import { DocumentTab } from './Tabs/Document/Document';
import { GeneralInfoTab } from './Tabs/GeneralInfo/GeneralInfo';
import { PromotionalProgramTab } from './Tabs/Promotion/PromotionalProgram';
import { SalesInfoTab } from './Tabs/SalesInfo/SalesInfo';
import { PurChaseInvoiceApi } from 'apis/PurchaseInvoiceApis';
import dayjs from 'dayjs';

enum TYPE_REQUEST {
  GET_PROMOTIONS_FROM_SAP = 'GET_PROMOTIONS_FROM_SAP',
  GET_ALLOCATE_PRICE_FROM_SAP = 'GET_ALLOCATE_PRICE_FROM_SAP',
  APPLY_PROMOTION_PROGRAMS = 'APPLY_PROMOTION_PROGRAMS',
  APPLY_PROMOTIONS_GET_ALLOCATE_PRICE = 'APPLY_PROMOTIONS_GET_ALLOCATE_PRICE',
}

const INIT_DEAL_PRICE_DATA = {
  dealPrice: null,
  salePrice: null,
  statusId: null,
  status: null,
  note: null,
};

const { Text } = Typography;

function SalesInvoiceDetailPage() {
  const navigate = useNavigate();

  const { setTitle, setBreadcrumb } = useTitle(),
    { user } = useUser();

  const firstRender = useRef(true);

  const queryParam = useQueryParam();
  const orderId = queryParam.get('orderId');
  const orderTypeId = queryParam.get('orderTypeId');

  const state = useMemo(() => {
    return { orderId, orderTypeId };
  }, [orderId, orderTypeId]);

  const [tabActive, setTabActive] = useState('1'),
    [customer, setCustomer] = useState({} as ICustomer),
    [orderData, setOrderData] = useState({} as IOrder),
    [goldSoldBuyOrderData, setGoldSoldBuyOrderData] = useState(
      {} as IGoldSoldBuyOrder | IGoldSoldYBuyOrder,
    ),
    [orderProcess, setOrderProcess] = useState({
      sap: [] as IOrderProcess[],
      dms: [] as IOrderProcess[],
    }),
    // [isOpenCombineOrdersModal, setIsOpenCombineOrdersModal] = useState(false),
    // [orderTempData, setOrderTempData] = useState(initLazySelectData),
    [isOpenCancelMergeModal, setIsOpenCancelMergeModal] = useState(false),
    [typeRequest, setTypeRequest] = useState<TYPE_REQUEST>(),
    [isMergeableOrder, setIsMergeableOrder] = useState(false),
    [isOpenDealPriceModal, setIsOpenDealPriceModal] = useState(false);

  // Sử dụng custom hook để quản lý promotion data
  const {
    promotionData,
    promotions,
    mergedData,
    updatePromotions,
    updatePromotionData,
    hasPromotionData,
    totalVouchers,
  } = usePromotionManager();

  const hasEInvoice = useMemo(() => {
    return (
      orderProcess.sap.find(
        (orderProcess) => orderProcess.processStatus?.id === 'EINVOICE',
      )?.status ?? false
    );
  }, [orderProcess]);

  const hasSODOBilling = useMemo(() => {
    return (
      orderProcess.sap.filter(
        (orderProcess) =>
          orderProcess?.status ||
          (!orderProcess.status && orderProcess.errorMessage === ''),
      ).length > 0
    );
  }, [orderProcess]);

  const isSAPDocumentCanceled = useMemo(() => {
    const isSOBillingSuccess = orderProcess.sap.some(
      (orderProcess) =>
        orderProcess.processStatus.id === 'SO' &&
        orderProcess.status &&
        orderProcess.errorMessage !== 'CANCEL',
    );
    const isCancelSuccess = orderProcess.sap.some(
      (orderProcess) =>
        orderProcess.status && orderProcess.errorMessage == 'CANCEL',
    );

    return !(isSOBillingSuccess && !isCancelSuccess);
  }, [orderProcess]);

  const isUnmergedOrder = useMemo(() => {
    const isSOSuccess =
      orderProcess.sap.length > 0 &&
      orderProcess.sap.every(
        (orderProcess) =>
          // orderProcess.processStatus.id === 'SO' &&
          // orderProcess.status &&
          orderProcess.errorMessage !== 'CANCEL',
      );
    const isSOInProgress = orderProcess.sap.some(
      (orderProcess) =>
        orderProcess.processStatus.id === 'SO' &&
        !orderProcess.status &&
        orderProcess.errorMessage === '',
    );
    const isMergedParent =
      orderData?.tOrderSeparation?.[0]?.statusId === 'OR02_03';

    return isSOSuccess || isSOInProgress || !isMergedParent;
  }, [orderProcess, orderData]);

  const isChildrenOrder = useMemo(() => {
    // đơn con gộp thì ko cho thao tác
    return (
      // orderData?.statusId === 'OR02_03' ||
      // // orderData?.mainOrderId !== null ||
      // (
      //   orderData?.tOrderSeparation?.filter(
      //     (order) => order.statusId === 'OR02_03',
      //   ) ?? []
      // ).length > 0
      orderData?.mainOrderId !== null && orderData?.statusId === 'OR02_03' // và nó phải là đơn tách con

      // hoặc mainOrderId == null
    );
  }, [orderData]);

  const isSendDocumentFailed = useMemo(() => {
    const [firstItem] = orderProcess.sap;

    return orderProcess.sap.length > 0 && !firstItem?.status;
  }, [orderProcess]);

  const isApproved = useMemo(() => {
    return (
      !orderData?.dealPrice?.statusId ||
      orderData?.dealPrice?.statusId === 'AP100_02'
    );
  }, [orderData]);

  const isOrderOfAccountant = useMemo(() => {
    if (user?.isAdmin || user?.isTreasurer) return false;

    return orderData?.createId === user?.id;
  }, [orderData, user]);

  const toResultJson = (item: any) => {
    return {
      results: (item?.results ?? []).map((item: any) => {
        return [
          'Key',
          'Item',
          'Saleorg',
          'Chanel',
          'Store',
          'Sloc',
          'Material',
          'Batch',
          'Quantity',
          'Type',
          'SaleDeal',
          'ConditionType',
          'ConditionValue',
          'PriceBefore',
          'PriceAfter',
          'Currency',
          'SaleUnit',
          'ProcessCost',
          'DiscountCost',
          'ConditionNum',
          'Program',
          'ConditionName',
          'Amount',
          'ItmNumber',
          'CondType',
          'CondValue',
          'CondPUnt',
          'CondUnit',
          'CondCount',
          'CondCountLong',
          'ManualEntries',
          'Inactive',
          'Flag',
          'DiscountItem',
          'CondStNo',
          'Voucher',
          'Seri',
          'Stt',
          'FAuto',
          'apply',
          'note',
        ].reduce((acc, key) => {
          if (key === 'apply') acc[key] = true;
          else acc[key] = item[key] || null;

          return acc;
        }, {} as any);
      }) as any,
    };
  };

  const { mutateAsync: getCustomerMutate, isPending: isGetCustomerPending } =
      useMutation({
        mutationFn: (customerId: string) =>
          SalesInfoApis.getCustomer(customerId),
        onSuccess: (res) => {
          if (res.data?.getCustomer)
            setCustomer(res.data?.getCustomer?.items[0]);
        },
        onError: (error) => {
          toastError(`Lỗi. ${error?.message ?? error}`);
        },
      }),
    { mutateAsync: getOrderMutate, isPending: isGetOrderPending } = useMutation(
      {
        mutationFn: (
          params: IGetOrderParams & {
            type?: 'init' | 'updatedCustomer' | 'allocatePriceFailure';
          },
        ) => SalesInfoApis.getOrder(params),
        onSuccess: (res: any, variables) => {
          if (res?.data?.getOrderData) {
            if (res?.data?.getOrderData?.items.length <= 0) return;

            const [item, ..._] = res?.data?.getOrderData?.items;

            setOrderData((prev) => ({
              ...item,
              dealPrice: prev?.dealPrice ?? INIT_DEAL_PRICE_DATA,
            }));

            getPromotionDataMutate(item?.id);

            if (variables?.type === 'allocatePriceFailure') return;

            getGoldSoldBuyOrderMutate(item?.id);

            if (variables?.type === 'init') {
              getOrderDealPriceMutate(item?.id);
              getCustomerMutate(item?.childCustomerId || item?.customerId);
            }

            if (variables?.type === 'updatedCustomer')
              getCustomerMutate(item?.childCustomerId || item?.customerId);

            return;
          }

          if (res?.errors.length > 0)
            toastError(`Lỗi. ${res?.errors[0]?.message}`);
        },
        onError: (error) => {
          toastError(`Lỗi. ${error?.message ?? error}`);
        },
      },
    ),
    {
      mutate: getGoldSoldBuyOrderMutate,
      isPending: isGetGoldSoldBuyOrderPending,
    } = useMutation({
      mutationFn: (orderId: string) => {
        return state?.orderTypeId === 'A109_01'
          ? SalesInfoApis.getGoldSoldBuyOrder(orderId)
          : SalesInfoApis.getGoldSoldYBuyOrder(orderId);
      },
      onSuccess: (res: any) => {
        if (res.data?.getGoldenBuyOrder) {
          setGoldSoldBuyOrderData(res.data?.getGoldenBuyOrder);
          return;
        }

        if (res?.errors) toastError(`Lỗi. ${res?.errors[0]?.message}`);
      },
      onError: (error) => {
        toastError(`Lỗi. ${error?.message}`);
      },
    }),
    {
      mutate: getOrderProcessMutate,
      isPending: isGetOrderProcesssMutatePending,
    } = useMutation({
      mutationFn: (
        params: IGetOrderProcessParams & { isEInvoicePending?: boolean },
      ) => SalesInfoApis.getOrderProcess(params),
      onSuccess: (res: any, variables) => {
        if (res?.data?.getOrderProcess) {
          const items: IOrderProcess[] = res.data.getOrderProcess?.items ?? [];

          if (
            variables.processStatus === 'sap' &&
            variables.isEInvoicePending
          ) {
            const EInvoiceCreated =
              items.find((item) => item?.processStatus?.id === 'EINVOICE')
                ?.status === true;

            EInvoiceCreated &&
              allocateUpdateJsonMutate({
                ...promotionData,
                IsEInvoicePending: false,
                successMessage: 'Tạo hóa đơn điện tử thành công',
              } as TAllocateUpdateJsonParams);
          }

          setOrderProcess((prev) => ({
            ...prev,
            [`${variables.processStatus}`]: items,
          }));
          return;
        }

        if (res?.errors)
          toastError(
            `Lỗi khi lấy thông tin quy trình đặt hàng của SAP. ${res?.errors[0]?.message}`,
          );
      },
      onError: (error) => {
        toastError(
          `Lỗi khi lấy thông tin quy trình đặt hàng của SAP. ${error?.message ?? error}`,
        );
      },
    }),
    {
      mutateAsync: getPromotionDataMutate,
      isPending: isGetPromotionDataPending,
    } = useMutation({
      mutationFn: (orderId: string) => SalesInfoApis.getPromotionData(orderId),
      onSuccess: (res: any) => {
        if (res?.id)
          try {
            updatePromotionData(JSON.parse(res?.metadata));
          } catch (error) {
            console.log(error);
          }
      },
      onError: (error) => {
        toastError(`Lỗi. ${error?.message ?? error}`);
      },
    }),
    {
      mutate: allocateUpdateJsonMutate,
      isPending: isAllocateUpdateJsonPending,
    } = useMutation({
      mutationFn: (params: TAllocateUpdateJsonParams) =>
        SalesInfoApis.allocateUpdateJson(params),
      onSuccess: async (res: any, variables) => {
        if (res?.message?.toLowerCase() === 'success') {
          const { isRefetch, afterMutateSuccess } = variables;

          !!isRefetch &&
            (await refetchOrder({
              ...(variables.type && { type: variables.type }),
            }));
          afterMutateSuccess && afterMutateSuccess();

          // variables?.successMessage && toastSuccess(variables?.successMessage);

          return;
        }

        toastError('Lấy giá phân bổ/CTKM từ SAP thất bại!');
      },
      onError: (error) => {
        toastError(
          `Áp dụng chương trình khuyến mãi không thành công. ${error?.message ?? error}`,
        );
      },
    }),
    { mutate: allocatePriceMutate, isPending: isAllocatePricePending } =
      useMutation({
        mutationFn: (
          params: IAllocatePriceParams & { afterMutateSuccess?: () => void },
        ) => {
          const {
            afterMutateSuccess,
            IsAllocatePricePending,
            IsEInvoicePending,
            PendingMessage,
            RequestTime,
            ...others
          } = params;

          return SalesInfoApis.allocatePrice(others as IAllocatePriceParams);
        },
        onSuccess: async (_, variables) => {
          const { Option } = variables;
          const isGetDataFromSAP = Option === 'A';
          const dealPrice = orderData?.dealPrice?.dealPrice || '';
          const newPromotionData = {
            ...(isGetDataFromSAP
              ? { ...promotionData, DealPrice: dealPrice }
              : { ...variables, DealPrice: dealPrice }),
            Option: Option,
            RequestTime: Date.now(),
            IsAllocatePricePending: true,
          };

          allocateUpdateJsonMutate({
            ...newPromotionData,
            isRefetch: false,
            afterMutateSuccess: () => {
              firstRender.current = true;
              updatePromotionData(newPromotionData);
              variables.afterMutateSuccess && variables.afterMutateSuccess();
            },
          } as TAllocateUpdateJsonParams);
        },
        onError: (error) => {
          toastError(`
            ${
              typeRequest === TYPE_REQUEST.APPLY_PROMOTION_PROGRAMS
                ? 'Áp dụng CTKM thất bại'
                : typeRequest === TYPE_REQUEST.GET_ALLOCATE_PRICE_FROM_SAP
                  ? 'Lấy giá phân bổ thất bại'
                  : 'Lấy CTKM từ SAP thất bại'
            }.
          ${error?.message ?? error}`);
        },
      }),
    { mutate: createOrderMutate, isPending: isCreateOrderPending } =
      useMutation({
        mutationFn: (params: IAllocatePriceParams) => {
          const {
            IsAllocatePricePending,
            IsEInvoicePending,
            PendingMessage,
            OutputDiscount,
            RequestTime,
            OutputItem,
            Return,
            ...others
          } = params;

          return SalesInfoApis.createOrder(others as IAllocatePriceParams);
        },
        onSuccess: (res: any) => {
          if (res?.message) {
            refetchOrderProcess();
            toastSuccess('Tạo SO, DO, GI thành công!');
            return;
          }

          toastError('Tạo SO, DO, GI thất bại!');
        },
        onError: (error: any) => {
          refetchOrderProcess();
          // Show error message from API response
          if (error?.response?.data?.message) {
            toastError(error.response.data.message);
          } else if (error?.message) {
            toastError(error.message);
          } else {
            toastError('Tạo SO, DO, GI thất bại!');
          }
          console.log(error?.message);
        },
      }),
    {
      mutate: getOrderTempByIdMutate,
      isPending: isGetOrderTempByIdMutatePending,
    } = useMutation({
      mutationFn: (
        params: IGetOrderTempByIdParams & { type: 'checkMerge' | 'getData' },
      ) => SalesInfoApis.getOrderTempById(params),
      onSuccess: (res: any, variables) => {
        if (res?.data?.getOrderNotMainOrderWeb) {
          // const data = res?.data?.getOrderNotMainOrderWeb;

          if (variables.type === 'checkMerge') {
            setIsMergeableOrder(
              (res?.data?.getOrderNotMainOrderWeb?.items ?? []).length > 0,
            );
            return;
          }

          // setOrderTempData({
          //   options: (
          //     res?.data?.getOrderNotMainOrder?.items?.filter(
          //       (item: any) => item?.id !== state?.orderId,
          //     ) ?? []
          //   ).map(
          //     (item: any) =>
          //       ({
          //         label: item?.id,
          //         value: item?.id,
          //       }) as DefaultOptionType,
          //   ),
          //   totalItems: data?.pageInfo?.totalItems ?? 0,
          // });
          // return;
        }

        if ((res?.errors ?? []).length >= 0)
          toastError(
            `Lỗi khi lấy danh sách đơn hàng để gộp. ${res?.errors?.[0]?.message}`,
          );
      },
      onError: (error) => {
        toastError(
          `Lỗi khi lấy danh sách đơn hàng để gộp. ${error?.message ?? error}`,
        );
      },
    }),
    // { mutate: mergeOrderMutate, isPending: isMergeOrderMutatePending } =
    //   useMutation({
    //     mutationFn: (params: IMergeOrderParams) =>
    //       SalesInfoApis.mergeOrder(params),
    //     onSuccess: (res: any) => {
    //       if (res?.data) {
    //         toastSuccess('Tạo đơn gộp thành công!');
    //         handleOnCancelCombineOrdersModal();
    //         setPromotionData({} as IPromotion);

    //         console.log(
    //           `${path.SALES_INVOICE_DETAIL}?orderId=${res?.data}&orderTypeId=${state?.orderTypeId}`,
    //         );

    //         navigate(
    //           `${path.SALES_INVOICE_DETAIL}?orderId=${res?.data}&orderTypeId=${state?.orderTypeId}`,
    //         );
    //         return;
    //       }

    //       toastError(`Tạo đơn gộp thất bại. ${res?.message}`);
    //     },
    //     onError: (error) => {
    //       toastError(`Gộp đơn thất bại. ${error?.message ?? error}`);
    //     },
    //   }),
    {
      mutate: cancelMergeOrderMutate,
      isPending: isCancelMergeOrderMutatePending,
    } = useMutation({
      mutationFn: (params: { orderId: string }) =>
        SalesInfoApis.cancelMergeOrder(params),
      onSuccess: (res) => {
        if (res) {
          toastSuccess('Hủy gộp đơn thành công!');
          navigate(path.SALES_INVOICE);
          return;
        }
      },
      onError: (error: any) => {
        toastError(
          `Hủy gộp đơn không thành công. ${error?.response?.data?.message ?? error}`,
        );
      },
    }),
    {
      mutate: allocatePriceSAPMutate,
      isPending: isAllocatePriceSAPMutatePending,
    } = useMutation({
      mutationFn: (params: IGetAllocatePriceSAP) =>
        SalesInfoApis.allocatePriceSap(params),
      onSuccess: (res: any, variables) => {
        if (res?.code === 1 && res?.data?.Key) {
          const response = res.data;
          const data = (
            variables.Option === 'A'
              ? {
                  ...promotionData,
                  OutputHeader: toResultJson(response?.OutputHeader),
                  OutputItem: toResultJson(response?.OutputItem),
                  OutputDiscount: {
                    results: (response?.OutputDiscount?.results ?? []).map(
                      (item: any) => {
                        const { __metadata, ...others } = item;

                        return { ...others, apply: true, note: null };
                      },
                    ),
                  },
                  ConditionInsert: {
                    results: (response?.OutputDiscount?.results ?? []).map(
                      (item: any) => ({
                        CondCount: item?.CondCount,
                        ConditionType: item?.ConditionType,
                        ConditionValue: item?.ConditionValue,
                        Item: item?.Item,
                        Key: item?.Key,
                      }),
                    ),
                  },
                  ConditionDelete: { results: [] },
                  IsAllocatePricePending: false,
                  successMessage: 'CTKM từ SAP đã được cập nhật!',
                }
              : {
                  ...promotionData,
                  IsAllocatePricePending: false,
                  successMessage:
                    typeRequest === TYPE_REQUEST.APPLY_PROMOTION_PROGRAMS
                      ? 'Áp dụng CTKM thành công!'
                      : typeRequest === TYPE_REQUEST.GET_ALLOCATE_PRICE_FROM_SAP
                        ? 'Lấy giá phân bổ thành công!'
                        : 'Áp dụng CTKM/Lấy giá phân bổ thành công!',
                }
          ) as IAllocatePriceParams & { successMessage?: string };
          firstRender.current = false;
          allocateUpdateJsonMutate({
            ...data,
            PendingMessage: '',
            isRefetch: true,
          });
          return;
        }

        if (res?.code === -1) {
          firstRender.current = false;
          allocateUpdateJsonMutate({
            ...promotionData,
            PendingMessage: res?.message,
            IsAllocatePricePending: false,
            isRefetch: true,
            afterMutateSuccess: () => {
              getPromotionDataMutate(orderData?.id);
            },
          } as TAllocateUpdateJsonParams);

          toastError(res?.message);

          return;
        }

        if (res?.code === 0) {
          if (
            promotionData?.RequestTime &&
            promotionData?.RequestTime < Date.now() - 300000
          ) {
            firstRender.current = false;
            allocateUpdateJsonMutate({
              ...promotionData,
              PendingMessage:
                res?.message ||
                (variables?.Option === 'A'
                  ? 'Lỗi khi lấy CTKM từ SAP'
                  : 'Lỗi khi lấy gia phân bổ'),
              IsAllocatePricePending: false,
              isRefetch: true,
              afterMutateSuccess: () => {
                getPromotionDataMutate(orderData?.id);
              },
            } as TAllocateUpdateJsonParams);

            toastError(res?.message);

            return;
          }
        }

        getPromotionDataMutate(variables.Key);
        // toastInfo(
        //   typeRequest === TYPE_REQUEST.APPLY_PROMOTION_PROGRAMS
        //     ? 'Đang áp dụng CTKM!'
        //     : typeRequest === TYPE_REQUEST.GET_ALLOCATE_PRICE_FROM_SAP
        //       ? 'Đang lấy giá phân bổ!'
        //       : typeRequest === TYPE_REQUEST.GET_PROMOTIONS_FROM_SAP
        //         ? 'Đang lấy CTKM từ SAP!'
        //         : 'Đang áp dụng CTKM/Lấy giá phân bổ!',
        // );
      },
      onError: (error, variables) => {
        console.log(error?.message ?? error);
        getPromotionDataMutate(variables.Key);

        // toastInfo(
        //   typeRequest === TYPE_REQUEST.APPLY_PROMOTION_PROGRAMS
        //     ? 'Đang áp dụng CTKM!'
        //     : typeRequest === TYPE_REQUEST.GET_ALLOCATE_PRICE_FROM_SAP
        //       ? 'Đang lấy giá phân bổ!'
        //       : typeRequest === TYPE_REQUEST.GET_PROMOTIONS_FROM_SAP
        //         ? 'Đang lấy CTKM từ SAP!'
        //         : 'Đang áp dụng CTKM/Lấy giá phân bổ!',
        // );
      },
    }),
    { mutate: printFileMutate, isPending: isPrintFileMutatePending } =
      useMutation({
        mutationFn: (params: IPrintFile) => SalesInfoApis.printFile(params),
        onSuccess: (res: any, variables) => {
          try {
            if (variables?.type === 'print') {
              const url = window.URL.createObjectURL(
                new Blob([res], { type: `application/pdf` }),
              );

              const newTab = window.open(url, '_blank');

              if (newTab)
                newTab.onload = () => {
                  newTab.print();
                };
              else
                toastError(
                  'Không thể mở tab mới. Vui lòng kiểm tra trình duyệt chặn popup.',
                );

              return;
            }

            const type = variables.type === 'pdf' ? 'pdf' : 'xlsx';

            const url = window.URL.createObjectURL(
              new Blob([res], { type: `application/${type}` }),
            );

            const link = document.createElement('a');
            link.href = url;
            link.download = `Bảng_kê_bán_hàng_${orderData.id}.${type}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          } catch (error) {
            toastError(`Lỗi khi xuất file ${variables.type}. ${error}`);
          }
        },
        onError: (error) => {
          toastError(`Lỗi. ${error?.message ?? error}`);
        },
      }),
    {
      mutate: getOrderDealPriceMutate,
      isPending: isGetOrderDealPriceMutatePending,
    } = useMutation({
      mutationFn: (orderId: string) => SalesInfoApis.getOrderDealPrice(orderId),
      onSuccess: (res) => {
        if (res?.data)
          setOrderData({
            ...orderData,
            dealPrice: res.data?.getOrderApprovalData ?? INIT_DEAL_PRICE_DATA,
          });
      },
      onError: (error: any) => {
        toastError(
          `Lỗi khi lấy thông tin giá bóng thỏa thuận. ${error?.message ?? error}`,
        );
      },
    }),
    { mutate: createEInvoiceMutate, isPending: isCreateEInvoiceMutatePending } =
      useMutation({
        mutationFn: (params: ICreateEInvoice) =>
          SalesInfoApis.createEInvoice(params),
        onSuccess: (res: any) => {
          if (res?.OUT_REFNO) {
            // toastInfo('Hệ thống đang tạo HĐĐT từ SAP!');
            const newPromotionData = {
              ...promotionData,
              IsEInvoicePending: true,
            };

            allocateUpdateJsonMutate({
              ...newPromotionData,
              afterMutateSuccess: () => updatePromotionData(newPromotionData),
            } as TAllocateUpdateJsonParams);
          }
        },
        onError: (error) => {
          toastError(`Lỗi khi tạo hóa đơn điện tử. ${error?.message ?? error}`);
        },
      }),
    { mutate: cancelSAPDocumentMutate, isPending: isCancelSAPDocumentPending } =
      useMutation({
        mutationFn: (orderId: string) =>
          SalesInfoApis.cancelSAPDocument(orderId),
        onSuccess: (res: any) => {
          if (res?.code === 0) {
            toastInfo('Hệ thống đang hủy chứng từ!');
            getOrderProcessMutate({
              orderId: orderData.id,
              processStatus: 'sap',
              isEInvoicePending: false,
            });
          }
        },
        onError: (error) => {
          toastError(`Lỗi khi hủy chứng từ SAP. ${error?.message ?? error}`);
        },
      }),
    { mutate: printEInvoiceMutate, isPending: isPrintEInvoiceMutatePending } =
      useMutation({
        mutationFn: (orderId: string) => SalesInfoApis.printEInvoice(orderId),
        onSuccess: async (res) => {
          try {
            if ((res?.data ?? []).length <= 0 && !res?.data?.[0]?.Link) {
              toastError('Bảng kê bán hàng chưa có hoá đơn điện tử!');
              return;
            }

            // const response = await axios.get(res?.data?.[0]?.Link, {
            //   responseType: 'blob',
            //   headers: {
            //     'Access-Control-Allow-Origin': '*',
            //     'Accept': 'application/json',
            //     'Access-Control-Allow-Credentials': 'true',
            //     'withCredentials': true,
            //   },
            // });
            // const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            const url = res?.data?.[0]?.Link;

            link.href = url;
            link.download = `Hoá_đơn_điền_tử_bảng_kê_bán_hàng_${orderData.id}.pdf`;
            link.target = '_blank';
            // document.body.appendChild(link);
            link.click();
            // document.body.removeChild(link);
            // window.URL.revokeObjectURL(url);
          } catch (error) {
            toastError(`Lỗi khi xuất file pdf. ${error}`);
          }
        },
        onError: (error) => {
          toastError(`Lỗi khi in hoá đơn điện tử. ${error?.message ?? error}`);
        },
      }),
    {
      mutate: reSendOrderToSAPMutate,
      isPending: isreSendOrderToSAPMutatePending,
    } = useMutation({
      mutationFn: (orderId: string) => SalesInfoApis.reSendOrderToSAP(orderId),
      onSuccess: () => {
        toastSuccess('Gửi lại đơn qua SAP thành công!');
      },
      onError: (error) => {
        toastError(`Lỗi khi gửi lại đơn qua SAP. ${error?.message ?? error}`);
      },
    }),
    {
      mutate: createDealPriceMutate,
      isPending: isCreateDealPriceMutatePending,
    } = useMutation({
      mutationFn: (params: CreateDealPrice) =>
        SalesInfoApis.createDealPrice(params),
      onSuccess: (res: any) => {
        if (res?.data) {
          toastSuccess('Gửi yêu cầu thoả thuận giá bóng thành công!');
          getOrderDealPriceMutate(orderData?.id);
          handleOnCancelDealPrice();

          return;
        }

        res?.errors.length > 0 &&
          toastError(
            `Lỗi khi yêu cầu thoả thuận giá bóng. ${res?.errors?.[0]?.message}`,
          );
      },
      onError: (error) => {
        toastError(
          `Lỗi khi yêu cầu thoả thuận giá bóng. ${error?.message ?? error}`,
        );
      },
    });

  useEffect(() => {
    if (!state?.orderId || !state?.orderTypeId) {
      navigate(path.SALES_INVOICE);
      return;
    }

    setTitle(state.orderId);
    setBreadcrumb([
      { title: 'Kế toán/thủ quỹ' },
      { title: 'Bảng kê bán hàng', href: path.SALES_INVOICE },
      { title: state.orderId },
    ]);

    refetchOrder({ type: 'init' });
    refetchOrderProcess();
  }, [state]);

  useEffect(() => {
    if (
      !user ||
      Object.keys(goldSoldBuyOrderData).length <= 0 ||
      !orderData?.id
    )
      return;

    const newPromotionData = {
      ...promotionData,
      OrderType:
        goldSoldBuyOrderData?.orderTempDetail?.[0]?.tproduct?.ttype?.id === 'Y'
          ? 'ZWY'
          : 'ZW2',
      SaleOrg: '5000',
      // SaleOffice: '5001',
      SaleOffice: user?.plantCode,
      Channel: '30',
      Division: '10',
      // SalePerson: 'E0114912',
      SalePerson: user?.employeeId ?? '',
      DealPrice: '',
      Key: orderData?.id,
      Customer: customer?.sapCustomerId,
      IsAllocatePricePending: promotionData?.IsAllocatePricePending || false,
      Item: {
        results: goldSoldBuyOrderData.orderTempDetail.map((order) => ({
          Key: orderData?.id,
          Item: order?.itemCode,
          Plant: user?.plantCode,
          Sloc: order?.tstorageLocation?.sloc,
          Material: order?.tproduct?.id,
          Batch: order?.tstorageLocation?.batch,
          Quantity: '1',
          ItemCategory: 'ZTAN',
          ProcessPrice: 0,
          ProcessPriceSAP: 0,
          TextScan: order?.tproduct?.textScan ?? '',
        })) as IPromotionResult['results'],
      },
    } as IPromotion;

    updatePromotionData(newPromotionData);
  }, [user, goldSoldBuyOrderData, orderData, customer]);

  useEffect(() => {
    if (!promotionData?.Key || !promotionData?.Option || !firstRender.current)
      return;

    if (promotionData.IsEInvoicePending) {
      getOrderProcessMutate({
        orderId: promotionData.Key,
        processStatus: 'sap',
        isEInvoicePending: promotionData.IsEInvoicePending,
      });

      firstRender.current = false;
    }

    if (!promotionData?.IsAllocatePricePending) return;

    if (!typeRequest)
      setTypeRequest(
        promotionData.Option === 'A'
          ? TYPE_REQUEST.GET_PROMOTIONS_FROM_SAP
          : TYPE_REQUEST.APPLY_PROMOTIONS_GET_ALLOCATE_PRICE,
      );

    // toastInfo(
    //   promotionData.Option === 'A'
    //     ? 'Đang lấy CTKM từ SAP!'
    //     : 'Đang lấy giá phân bổ!',
    // );

    setTimeout(() => {
      allocatePriceSAPMutate({
        Key: promotionData.Key,
        Option: promotionData.Option,
      });
    }, 5000);

    // firstRender.current = false;
  }, [promotionData, firstRender]);

  useEffect(() => {
    if (!orderData?.id || !customer?.id || !state?.orderTypeId) return;

    getOrderTempByIdMutate({
      pageSize: 1,
      customerId: customer?.id,
      searchValue: orderData.id,
      // orderId: orderData.id,
      orderTypeId: state?.orderTypeId,
      type: 'checkMerge',
    });
  }, [orderData, customer, state]);

  useEffect(() => {
    handleChangeCTKM();
  }, [promotions]);

  const refetchOrder = useCallback(
    async (params: {
      type?: 'init' | 'updatedCustomer' | 'allocatePriceFailure';
    }) => {
      if (!state?.orderId || !state?.orderTypeId) return;

      await getOrderMutate({
        orderId: state.orderId,
        orderTypeId: state.orderTypeId,
        type: params.type,
      });
    },
    [state],
  );

  const refetchOrderProcess = useCallback(() => {
    ['dms', 'sap'].map((processStatus) => {
      if (!state?.orderId) return;

      getOrderProcessMutate({ orderId: state.orderId, processStatus });
    });
  }, [state]);

  const handleGetDataFromSAP = useCallback(
    (params: IAllocatePriceParams & { afterMutateSuccess?: () => void }) => {
      if (!promotionData) return;

      setTypeRequest(
        params.Option === 'A'
          ? TYPE_REQUEST.GET_PROMOTIONS_FROM_SAP
          : TYPE_REQUEST.GET_ALLOCATE_PRICE_FROM_SAP,
      );

      allocatePriceMutate(params);
    },
    [promotionData, goldSoldBuyOrderData],
  );

  function handleChangeCTKM() {
    try {
      if (promotionData?.Option === 'M' && promotions.changeCTKM == true) {
        const conditionInsert: IPromotionResult['results'] = [];
        const conditionDelete: IPromotionResult['results'] = [];

        promotions.SAPPromotions.forEach((item) => {
          item.records.forEach((item) => {
            const condition = {
              CondCount: item.CondCount,
              ConditionType: item.ConditionType,
              ConditionValue: item.ConditionValue,
              Item: item.Item,
              Key: item.Key,
            };

            if (item.apply) {
              conditionInsert.push(condition);
              return;
            }

            conditionDelete.push(condition);
          });
        });

        const params = {
          ...promotionData,
          Option: 'A',
          ConditionInsert: { results: conditionInsert },
          ConditionDelete: { results: conditionDelete },
          OutputDiscount: {
            results: (promotionData?.OutputDiscount?.results ?? []).map(
              (result) => {
                const isNotApply =
                  conditionInsert.findIndex(
                    (condition) =>
                      condition?.Item === result?.Item &&
                      condition?.ConditionType === result?.ConditionType,
                  ) < 0;

                return { ...result, apply: !isNotApply };
              },
            ),
          },
          PromoHeader: {
            results: [
              ...promotions.vouchers,
              ...promotions.manualVouchers.reduce((acc, item) => {
                if (item?.ConditionType.includes('H')) {
                  item?.records.forEach((item) => {
                    try {
                      const { id, ...others } = item;
                      acc.push(others);
                    } catch {
                      acc.push(item);
                    }
                  });
                }

                return acc;
              }, [] as any),
            ],
          },
          Voucher: {
            results: promotions.vouchers.map((item) => {
              return {
                ...item,
                Item: '000000',
                Key: orderData?.id,
              };
            }) as any,
          },
          PromoItem: {
            results: promotions.manualVouchers.reduce((acc, item) => {
              item.records.forEach((item) => {
                try {
                  if (item?.ConditionType?.includes('I')) {
                    const { id, ...others } = item;
                    acc.push(others);
                  }
                } catch (error) {
                  console.log(error);
                  acc.push(item);
                }
              });

              return acc;
            }, [] as Result[]),
          },
        } as IAllocatePriceParams;
        allocateUpdateJsonMutate({
          ...params,
          isRefetch: true,
        });
      }
    } catch (error) {
      console.error('Error handling change CTKM:', error);
      toastError(`Lỗi khi thay đổi CTKM. ${error}`);
    }

    // setPromotions(promotion);
  }

  const onApplyVouchers = useCallback((params: IAllocatePriceParams) => {
    setTypeRequest(TYPE_REQUEST.APPLY_PROMOTION_PROGRAMS);
    allocatePriceMutate(params);
  }, []);

  const handleCreateEInvoice = useCallback(() => {
    if (!orderData?.id) return;

    const BILL = orderProcess.sap.find(
      (item) => item.processStatus.id === 'BILL',
    );

    if (!BILL || !BILL?.processId) return;

    createEInvoiceMutate({ orderId: orderData.id, PVBELN: BILL.processId });
  }, [orderData, orderProcess]);

  const handleCancelSAPDocument = useCallback(() => {
    if (!orderData) return;

    cancelSAPDocumentMutate(orderData.id);
  }, [orderData]);

  const optionMenu = useMemo(() => {
    return {
      generalInfoTab: [
        // {
        //   label: 'Gộp đơn',
        //   key: '1',
        //   onClick: () => setIsOpenCombineOrdersModal(true),
        //   disabled: !isMergeableOrder,
        // },
        {
          label: 'Tách đơn',
          key: '2',
          onClick: () => console.log('Tách đơn'),
        },
        {
          label: 'Thủ kho xác nhận',
          key: '3',
          onClick: () => console.log('Thủ kho xác nhận'),
        },
        {
          label: 'Thủ quỹ xác nhận',
          key: '4',
          onClick: () => console.log('Thủ quỹ xác nhận'),
        },
      ],
      salesInfoTab: [
        // {
        //   label: 'Thủ kho xác nhận',
        //   key: 'storekeeperConfirm',
        //   onClick: () => console.log('Thủ kho xác nhận'),
        //   disabled: isChildrenOrder,
        // },
        // {
        //   label: 'Thủ quỹ xác nhận',
        //   key: 'treasurerConfirm',
        //   onClick: () => console.log('Thủ quỹ xác nhận'),
        //   disabled: isChildrenOrder,
        // },
        // {
        //   label: 'Thương lượng giá',
        //   key: 'dealPrice',
        //   onClick: () => console.log('Thương lượng giá'),
        //   disabled: isChildrenOrder,
        // },
        // {
        //   label: 'Gộp đơn',
        //   key: 'mergeOrder',
        //   onClick: () => setIsOpenCombineOrdersModal(true),
        //   disabled:
        //     !isMergeableOrder ||
        //     orderProcess.sap.some(
        //       (orderProcess) =>
        //         orderProcess.processStatus.id === 'SO' &&
        //         orderProcess.status &&
        //         orderProcess.errorMessage !== 'CANCEL',
        //     ) ||
        //     orderProcess.sap.some(
        //       (orderProcess) =>
        //         orderProcess.processStatus.id === 'SO' &&
        //         !orderProcess.status &&
        //         orderProcess.errorMessage === '',
        //     ),
        // },
        {
          label: 'Thương lượng giá bóng',
          key: 'dealPrice',
          onClick: () => setIsOpenDealPriceModal(true),
          disabled:
            !isApproved ||
            isChildrenOrder ||
            hasSODOBilling ||
            promotionData.IsEInvoicePending ||
            promotionData.IsAllocatePricePending ||
            !isOrderOfAccountant,
        },
        {
          label: 'Tạo hóa đơn điện tử',
          key: 'createEInvoice',
          onClick: handleCreateEInvoice,
          disabled:
            promotionData.IsEInvoicePending ||
            orderProcess.sap.findIndex(
              (orderProcess) =>
                orderProcess.processStatus.id === 'BILL' &&
                orderProcess?.status,
            ) < 0 ||
            orderProcess.sap.findIndex(
              (orderProcess) =>
                orderProcess.processStatus.id === 'EINVOICE' ||
                (orderProcess.errorMessage === '' && !orderProcess.status),
            ) >= 0 ||
            orderProcess.sap.findIndex(
              (orderProcess) =>
                orderProcess?.errorMessage === 'CANCEL' && orderProcess?.status,
            ) >= 0 ||
            isChildrenOrder ||
            !isOrderOfAccountant,
        },
        {
          label: 'In hoá đơn điện tử',
          key: 'printEInvoice',
          onClick: () => printEInvoiceMutate(orderData.id),
          disabled:
            isChildrenOrder ||
            !hasEInvoice ||
            orderProcess?.sap?.find(
              (item) => item?.processStatus?.id === 'EINVOICE',
            )?.url === '' ||
            orderProcess.sap.findIndex(
              (orderProcess) =>
                orderProcess?.errorMessage === 'CANCEL' && orderProcess?.status,
            ) >= 0,
        },
        {
          label: 'Gửi lại đơn',
          key: 'reSendOrderToSAP',
          onClick: () => reSendOrderToSAPMutate(orderData?.id),
          disabled:
            isChildrenOrder || !isSendDocumentFailed || !isOrderOfAccountant,
        },
        {
          label: 'Huỷ gộp',
          key: 'cancelOrder',
          onClick: () => setIsOpenCancelMergeModal(true),
          danger: true,
          disabled:
            isUnmergedOrder ||
            isChildrenOrder ||
            promotionData.IsEInvoicePending ||
            promotionData.IsAllocatePricePending ||
            !isOrderOfAccountant,
        },
        {
          label: 'Huỷ chứng từ SAP',
          key: 'cancelSAP',
          onClick: handleCancelSAPDocument,
          danger: true,
          disabled:
            isSAPDocumentCanceled || isChildrenOrder || !isOrderOfAccountant,
        },
      ],
    } as { [key: string]: MenuProps['items'] };
  }, [
    orderData,
    isApproved,
    hasEInvoice,
    orderProcess,
    promotionData,
    hasSODOBilling,
    isUnmergedOrder,
    isChildrenOrder,
    isOrderOfAccountant,
    // isMergeableOrder,
    isSendDocumentFailed,
    isSAPDocumentCanceled,
    printEInvoiceMutate,
    handleCreateEInvoice,
    handleCancelSAPDocument,
  ]);

  const exportMenuProps = useMemo(() => {
    return {
      items: [
        {
          label: 'Xuất excel',
          key: '1',
          onClick: () =>
            printFileMutate({
              type: 'excel',
              orderId: orderData.id,
              orderTypeId: orderData.orderTypeId,
              isMon:
                goldSoldBuyOrderData.orderTempDetail?.[0]?.tproduct?.ttype
                  ?.id === 'M',
            }),
        },
        {
          label: 'Xuất PDF',
          key: '2',
          onClick: () => {
            printFileMutate({
              type: 'pdf',
              orderId: orderData.id,
              orderTypeId: orderData.orderTypeId,
              isMon:
                goldSoldBuyOrderData.orderTempDetail?.[0]?.tproduct?.ttype
                  ?.id === 'M',
            });
          },
        },
        {
          label: 'In',
          key: '3',
          onClick: () =>
            printFileMutate({
              type: 'print',
              orderId: orderData.id,
              orderTypeId: orderData.orderTypeId,
              isMon:
                goldSoldBuyOrderData.orderTempDetail?.[0]?.tproduct?.ttype
                  ?.id === 'M',
            }),
        },
      ],
    } as MenuProps;
  }, [orderData, goldSoldBuyOrderData]);

  const orderStatus: { status: boolean; message: string } = useMemo(() => {
    const status = { status: true, message: 'Mới tạo' };

    if (
      !promotionData?.OutputDiscount &&
      !promotionData?.IsAllocatePricePending
    )
      return status;

    const sortedData = [...orderProcess?.sap]?.sort((a, b) => {
      const dateA = new Date(a?.createDate || '').getTime();
      const dateB = new Date(b?.createDate || '').getTime();
      return dateB - dateA;
    });

    const isOptionA = promotionData?.Option === 'A';
    const [firstOrderProcess] = sortedData;
    const orderProcessValue = firstOrderProcess?.processStatus?.id;
    const message = firstOrderProcess?.errorMessage ?? '';
    const pendingMessage = promotionData?.PendingMessage ?? '';

    if (firstOrderProcess) {
      if (firstOrderProcess?.status) {
        status.message =
          message === 'CANCEL'
            ? `Hủy ${orderProcessValue} thành công`
            : `Tạo ${orderProcessValue} thành công`;

        return status;
      }

      return message === ''
        ? { message: `Đang chờ tạo ${orderProcessValue}`, status: true }
        : {
            message:
              message === 'CANCEL'
                ? `Hủy ${orderProcessValue} thất bại`
                : `Tạo ${orderProcessValue} thất bại`,
            status: false,
          };
    }

    if (promotionData?.IsAllocatePricePending)
      status.message = isOptionA
        ? 'Đang lấy CTKM từ SAP'
        : 'Đang lấy giá phân bổ';
    else {
      status.message = isOptionA
        ? pendingMessage === ''
          ? 'Đã lấy CTKM từ SAP'
          : 'Lỗi khi lấy CTKM từ SAP'
        : pendingMessage === ''
          ? 'Đã lấy giá phân bổ'
          : 'Lỗi khi lấy giá phân bổ';

      status.status = pendingMessage === '' ? true : false;
    }

    return status;
  }, [promotionData, orderProcess]);

  // function isOfType(data: any) {
  //   const keysOfData = Object.keys(data);
  //   return data
  //     ? Object.entries(data).every(([key, _]) => keysOfData.includes(key))
  //     : false;
  // }

  // const isGoldSoldBuyOrder = useCallback((data: any) => {
  //   return isOfType(data);
  //   // return isOfType<IGoldSoldBuyOrder>(data);
  // }, []);

  const handleCreateOrderWithServerParams = async (
    params: IAllocatePriceParams,
  ) => {
    try {
      const res = (await getPromotionDataMutate(orderData.id)) as {
        metadata?: string;
      };
      if (!res?.metadata) {
        toastError('Không nhận được metadata từ server!');
        return;
      }
      const serverParams = JSON.parse(res.metadata);
      const mergedParams = {
        ...serverParams,
        Option: 'M',
        // UniqueCode: Date.now(),
      };
      console.log('serverParams', mergedParams);
      if (mergedParams.OrderType != 'ZWY') {
        const res = await PurChaseInvoiceApi.getMarketGoldPrice(
          convertDateToBE(dayjs()),
        );
        const marketGoldPrice = res?.data?.getDataMarketGoldPrice?.items?.[0];

        if (marketGoldPrice.salePrice != orderData?.marketGoldPrice) {
          toastError(`Giá vàng đã thay đổi, vui lòng lấy phân bổ lại!`);
          return;
        }

        mergedParams['GoldPrice'] = orderData?.marketGoldPrice.toString();
      }
      await createOrderMutate(mergedParams);
    } catch (error) {
      toastError('Lỗi khi lấy param từ server hoặc tạo đơn hàng');
    }
  };

  const tabItems: TabsProps['items'] = useMemo(() => {
    return [
      {
        key: '1',
        label: 'Thông tin chung',
        children: (
          <GeneralInfoTab
            customer={customer}
            orderData={orderData}
            orderStatus={orderStatus}
            // exportMenuProps={exportMenuProps}
            optionMenuItems={optionMenu?.generalInfoTab}
            orderSAPProcess={orderProcess.sap}
            goldSoldBuyOrderData={goldSoldBuyOrderData}
            // goldSoldBuyOrderData={
            //   isGoldSoldBuyOrder(goldSoldBuyOrderData)
            //     ? goldSoldBuyOrderData
            //     : ({} as IGoldSoldBuyOrder)
            // }
          />
        ),
      },
      {
        key: '2',
        label: 'Thông tin bán hàng',
        children: (
          <SalesInfoTab
            customer={customer}
            orderData={orderData}
            hasEInvoice={hasEInvoice}
            orderStatus={orderStatus}
            promotionData={promotionData}
            hasSODOBilling={hasSODOBilling}
            promotion={{ data: mergedData }}
            exportMenuProps={exportMenuProps}
            isChildrenOrder={isChildrenOrder}
            isOrderOfAccountant={isOrderOfAccountant}
            optionMenuItems={optionMenu?.salesInfoTab}
            goldSoldBuyOrderData={goldSoldBuyOrderData}
            refetchOrder={refetchOrder}
            onCreateOrder={handleCreateOrderWithServerParams}
            onTakeAllocatePrice={handleGetDataFromSAP}
          />
        ),
      },
      {
        key: '3',
        label: 'Chương trình khuyến mãi',
        children: (
          <PromotionalProgramTab
            orderData={orderData}
            hasEInvoice={hasEInvoice}
            hasSODOBilling={hasSODOBilling}
            isChildrenOrder={isChildrenOrder}
            isOrderOfAccountant={isOrderOfAccountant}
            goldSoldBuyOrderData={goldSoldBuyOrderData}
            promotion={{
              data: promotionData,
              isPending: isGetPromotionDataPending,
            }}
            table={{
              data: promotions,
              setData: updatePromotions,
            }}
            onApplyVouchers={onApplyVouchers}
            onTakeSAPPromotionProgram={handleGetDataFromSAP}
          />
        ),
      },
      {
        key: '4',
        label: 'Chứng từ',
        children: (
          <DocumentTab
            data={orderProcess}
            refetch={() => {
              getOrderProcessMutate({
                orderId: promotionData.Key,
                processStatus: 'sap',
                isEInvoicePending: promotionData.IsEInvoicePending,
              });
            }}
          />
        ),
      },
    ];
  }, [
    customer,
    orderData,
    promotions,
    optionMenu,
    orderStatus,
    hasEInvoice,
    orderProcess,
    promotionData,
    isChildrenOrder,
    hasSODOBilling,
    isOrderOfAccountant,
    exportMenuProps,
    goldSoldBuyOrderData,
    isGetPromotionDataPending,
  ]);

  const handleTabChange = useCallback((key: string) => {
    setTabActive(key);
  }, []);

  // const handleOnCancelCombineOrdersModal = useCallback(() => {
  //   setIsOpenCombineOrdersModal(false);
  // }, []);

  const handleOnCancelMergeOrdersModal = useCallback(() => {
    setIsOpenCancelMergeModal(false);
  }, []);

  const handleOnOkCancelMergeOrdersModal = useCallback(() => {
    if (!state?.orderId) return;

    cancelMergeOrderMutate({ orderId: state?.orderId });
  }, [state]);

  // const handleOnOkCombineOrdersModal = useCallback(
  //   (formValues: any) => {
  //     if (Object.keys(orderData).length <= 0 || !formValues?.orderId) return;

  //     mergeOrderMutate({ orderIds: `${orderData?.id},${formValues.orderId}` });
  //   },
  //   [orderData],
  // );

  // const handleOnSelectOrderScrollChange = useCallback(
  //   (params: ILazySelectParams) => {
  //     const { pageSize, value } = params;
  //     if (!state?.orderId || !state?.orderTypeId) return;

  //     getOrderTempByIdMutate({
  //       pageSize,
  //       customerId: customer?.id,
  //       // customerId: customer?.sapCustomerId ?? customer?.id,
  //       orderId: value,
  //       orderTypeId: state?.orderTypeId,
  //       type: 'getData',
  //     });
  //   },
  //   [customer],
  // );

  const handleOnOkDealPrice = useCallback(
    (values: any) => {
      createDealPriceMutate({
        orderId: orderData?.id,
        ...values,
        note: values?.note ?? '',
      });
    },
    [orderData],
  );

  const handleOnCancelDealPrice = useCallback(() => {
    setIsOpenDealPriceModal(false);
  }, []);

  return (
    <Spin
      spinning={
        isGetCustomerPending ||
        isGetOrderPending ||
        isGetGoldSoldBuyOrderPending ||
        isGetOrderProcesssMutatePending ||
        isAllocatePricePending ||
        isAllocateUpdateJsonPending ||
        isCreateOrderPending ||
        isAllocatePriceSAPMutatePending ||
        isPrintFileMutatePending ||
        isGetOrderDealPriceMutatePending ||
        isCreateEInvoiceMutatePending ||
        isCancelSAPDocumentPending ||
        isGetOrderTempByIdMutatePending ||
        isPrintEInvoiceMutatePending ||
        isreSendOrderToSAPMutatePending ||
        isCreateDealPriceMutatePending
      }
    >
      <Row justify="end" align="middle" gutter={[16, 16]}>
        <Col
          className={classNames(
            'w-max rounded-[100px] py-[4px] px-[11.5px]',
            orderStatus.status ? 'bg-[#EAF2FF] ' : 'bg-[#ffeaeb]',
          )}
        >
          <p className="text-sm font-normal">
            <span>Trạng thái:</span>{' '}
            <span
              className={classNames(
                'font-bold',
                orderStatus?.status ? 'text-[#0077CC]' : 'text-[#eb3455]',
              )}
            >
              {orderData.isSyncFromSap
                ? orderData.orderStatusSyn
                : orderStatus?.message}
            </span>
          </p>
        </Col>
        <Col>
          <Text>
            Đơn vị: <strong>VND</strong>
          </Text>
        </Col>
      </Row>
      <Content>
        <Tabs
          defaultActiveKey={tabActive}
          onChange={handleTabChange}
          items={tabItems.map((tab) => {
            const classes = classNames('text-[#757779]', 'text-sm', {
              '!text-[#0077CC]': tabActive === tab.key,
            });

            return {
              key: tab.key,
              label: <p className={classes}>{tab.label}</p>,
              children: tab.children,
            };
          })}
        />
      </Content>
      {/* <CombineOrdersModal
        data={orderTempData}
        loading={isGetOrderTempByIdMutatePending || isMergeOrderMutatePending}
        open={isOpenCombineOrdersModal}
        onOk={handleOnOkCombineOrdersModal}
        onCancel={handleOnCancelCombineOrdersModal}
        onSelectScrollChange={handleOnSelectOrderScrollChange}
      /> */}
      <Modal
        title="Thông báo"
        open={isOpenCancelMergeModal}
        className="min-w-[600px]"
        onCancel={handleOnCancelMergeOrdersModal}
        footer={
          <div className="flex justify-end mt-4 gap-2">
            <Button
              handleOnclick={handleOnCancelMergeOrdersModal}
              customClass="!border-transparent !text-neutral-30 !bg-transparent"
              title="Quay lại"
            />
            <Button
              disabled={isCancelMergeOrderMutatePending}
              fill
              title="Xác nhận"
              handleOnclick={handleOnOkCancelMergeOrdersModal}
            />
          </div>
        }
      >
        <p>Bạn có chắc muốn hủy gộp đơn?</p>
      </Modal>
      <DealPriceModal
        open={isOpenDealPriceModal}
        loading={isCreateDealPriceMutatePending}
        currentMarketGoldPrice={orderData?.marketGoldPrice}
        onOk={handleOnOkDealPrice}
        onCancel={handleOnCancelDealPrice}
      />
    </Spin>
  );
}

export default SalesInvoiceDetailPage;
